import { createActor, fromPromise, setup } from 'xstate'
import {
  autoPlayService,
  ErrorType,
  ga4Info,
  GodziDropResultType,
  preciseAdd,
  setErrorPopupData,
  sleep,
  useAutoPlayStore,
  usePlayerStore,
  useTurboStore,
  useVoucherStore,
} from '@slot-nx/core'
import { winningService } from '../winning/WinningService'
import { spinService } from '../game'
import { GameStatus, useGameStore } from '../game/store/GameStore'
import { placeOrderService } from '../placeOrder'
import { tableService } from '../table/tableService'
import { winningManager } from '../winning/WinningManager'
import { useGodziDropStore } from '../godzi/store/godziDropStore'
import { Drop, GameResult, Multiplier } from '../game/model'
import { useOngoingGameStore } from '../game/store/ongoingGameStore'
import { endGameService } from '../endGame'
import { freeGameService } from '../freeGame'
import { godziDropService } from '../godzi'
import { videoRecordService } from '../videoRecording'
import { isPlayerHasEnoughBalance } from '../spin/SpinUtil'
import { BetOptionType } from '../placeOrder/utils/stakeUtils'
// import { useGodziDropStore } from '../domain/godzi/store/godziDropStore'
// import { godziDashboardService } from '../domain/godzi/service/GodziDashboardService'

/** 本次 spin 是否為斷線重連後的 ongoing game 重播；重播結束回到 init game 時，用來判斷要不要接著進 voucher mode */
let isReplayingOngoingGame = false

/**
 * 記錄一次派彩到 session 帳（注額已在 PlaceOrderService 下單時記過）。
 *
 * 必須掛在「每轉保證會執行」的點上，不能掛在 init game——autoplay 時
 * winning/collectCombo 會直接跳回 spinning，完全不經過 init game。
 */
function recordSessionPayout(payout: number) {
  if (payout <= 0) return
  usePlayerStore.getState().updateSessionWinLose(0, payout)
}

async function payoutMultiplierForMainGame(payout: number, multiplierList: Multiplier[]) {
  await winningService.collectMultiplier(multiplierList)
  await winningService.payoutMultiplier(payout)
}

/** 免費遊戲多播一段「累積 totalMultiplier」的表演，主遊戲沒有這個概念 */
async function payoutMultiplierForFreeGame(payout: number, multiplierList: Multiplier[]) {
  await winningService.collectTotalMultiplier()
  await winningService.collectMultiplier(multiplierList)
  await sleep(200)
  await winningService.bgPayoutMultiplier(payout)
}

/** 主遊戲 winning 收尾：voucher 記帳、godzi 中獎取消 autoplay、autoplay 節奏延遲——免費遊戲不適用 */
async function finishMainGameWinning(payout: number) {
  const store = useGameStore.getState()
  const godziPrizeType = useGodziDropStore.getState().type
  if (store.gameStatus === GameStatus.Voucher) {
    useVoucherStore.setState(state => ({
      totalPayout: preciseAdd(state.totalPayout, payout),
    }))
  }
  if (godziPrizeType !== GodziDropResultType.None) {
    autoPlayService.cancel()
  }
  // 主遊戲這一轉沒有觸發 free game 時，後端本來就是即時結算（balance 不會是 -1），
  // 這裡把結算後的餘額同步進畫面，不用等到下一轉開始前的閘門才更新
  const triggersFreeGame = store.gameResult.fgResultList.length > 0
  if (!triggersFreeGame) {
    await endGameService.endGameIfNeeded()
    usePlayerStore.getState().updateBalanceToSettledAmount()
  }
}

/**
 * 若這一輪落地的盤面（finalTable）上有 collection symbol，播放 pot 收集表演。
 * 從獨立的 collectCombo state 移除後改由 onSpinning/onFreeSpinning（無 tumble）
 * 或 onRemoveing 最後一次消去（有 tumble）呼叫
 */
async function collectComboIfNeeded() {
  const comboList = tableService.checkCollectCombo()
  if (comboList.length === 0) return

  const gameStore = useGameStore.getState()
  const gameResult = gameStore.getGameResult()
  const { fgAddTimes } = gameResult
  const { isFreeGame } = gameStore
  const isRetrigger = isFreeGame && fgAddTimes > 0
  const triggersFreeGame = gameStore.gameResult.fgResultList.length > 0

  // 沒觸發 free game 時，pot 收集只是純表演——資料（level/collectNum）
  // 在 tableService.collectCombo 內是同步算完並落地的，播飛行動畫不用擋流程
  if (!triggersFreeGame) {
    tableService.collectCombo(comboList)
    return
  }

  // 有觸發 free game：potInFg 會接著在同一批 pot spine 上播動畫，
  // 必須先等 collectCombo 的表演播完，避免兩段動畫互相打斷
  await tableService.collectCombo(comboList)
  await tableService.potInFg(gameResult.freeGameType, isRetrigger)
  if (isRetrigger) {
    await freeGameService.playRetrigger(fgAddTimes, gameResult.freeGameType)
  }
}

/**
 * 有 tumble 又有 multiplier symbol 時，collect 表演要延後到 onMainGameWinning/onFreeGameWinning
 * 表演完 multiplier 收集 + winbar 跑分之後才觸發，避免跟這兩段動畫互相搶畫面。
 * 觸發 retrigger（fgAddTimes > 0）時同樣要延後——collect 完會接著播 retrigger 場景動畫，
 * 提早在 onRemoveing 觸發會跟 winbar 跑分撞在一起。
 * 其餘情況維持在 onRemoveing 最後一次消去時就觸發（見該處呼叫）。
 */
function shouldDeferCollectCombo(dropList: unknown[], multiplierList: unknown[], fgAddTimes: number) {
  return (dropList.length > 1 && multiplierList.length > 0) || fgAddTimes > 0
}

async function collectComboAfterMultiplierWinIfNeeded(dropList: unknown[], multiplierList: unknown[], fgAddTimes: number) {
  if (shouldDeferCollectCombo(dropList, multiplierList, fgAddTimes)) {
    await collectComboIfNeeded()
  }
}

const machine = setup({
  types: {} as {
    events:
      | { type: 'tutorial end' }
      | { type: 'initVoucher finished' }
      | { type: 'close godzi dashboard' }
      | { type: 'check remain voucher' }
      | { type: 'start spin' }
      | { type: 'free game start spin' }
      | { type: 'close fg total win' }
      | { type: 'start enter godzi drop' }
      | { type: 'exit godzi drop for ratio prize' }
      | { type: 'exit godzi drop' }
      | { type: 'start godzi spin flow' }
      | { type: 'show godziwin finished' }
      | { type: 'show voucher total win' }
  },
  actors: {
    onSpinning: fromPromise(async () => {
      winningService.init()
      const { isPlaying } = useAutoPlayStore.getState()
      const { isTurbo } = useTurboStore.getState()
      const { isExpand, dropIndex } = useGameStore.getState()
      const gameStatus = useGameStore.getState().gameStatus
      const { isOnGoingGame } = useOngoingGameStore.getState()
      const isNormalSpin = !isOnGoingGame && (gameStatus === GameStatus.Normal || gameStatus === GameStatus.Voucher)
      // autoplay 連續轉時 winning/collectCombo 會直接跳回這裡、不經過 init game，
      // 所以要在這裡（gameResult 被這次結果覆蓋之前）先記下上一輪有沒有派彩
      const hadPreviousPayout = useGameStore.getState().gameResult.mgResult.payout > 0
      if (isPlaying) {
        autoPlayService.reduceRemain()
        ga4Info.recordAutoPlay()
        if (isNormalSpin) {
          // autoplay：轉輪開始轉之前，先確保上一單 EndGame 結算完成、把結算後的餘額
          // 套用到畫面上，讓玩家有機會看到餘額跑動——不要讓轉輪動畫搶在結算/餘額更新前面。
          // 沒中獎就不用等，balance 動畫本身很短，沒東西可看，硬等會拖慢 autoplay 節奏
          await endGameService.ensureSettledBeforeNextSpin()
          usePlayerStore.getState().updateBalanceToSettledAmount()
          if (hadPreviousPayout) {
            await sleep(500)
          }
        }
      }
      if (isTurbo) ga4Info.recordSpeedTurboMode()
      if (isExpand) await tableService.removeExpandTable()
      await spinService.spinOut()
      const loadingTimer = setTimeout(() => {
        useGameStore.setState({ isShowSpinLoading: true })
      }, 3000)
      let gameResult
      const { consumeOngoingGameResult } = useOngoingGameStore.getState()
      if (isOnGoingGame) {
        // 重新整理恢復的未結算單：直接重播已還原的結果，不重新下注
        isReplayingOngoingGame = true
        gameResult = consumeOngoingGameResult() as GameResult
      }
      else if (isNormalSpin) {
        // 上一單的 EndGame 必須完成才能下注下一轉，未完成會丟錯中斷這次 spin；
        // autoplay 情況下上面已經等過一次，這裡是安全的 no-op
        await endGameService.ensureSettledBeforeNextSpin()
        gameResult = await placeOrderService.placeOrder()
      }
      else {
        godziDropService.prepareGodziSpinData()
        gameResult = useGameStore.getState().gameResult
      }
      clearTimeout(loadingTimer)
      useGameStore.setState({ isShowSpinLoading: false })
      // 主遊戲派彩：結果一回來就記，這裡每轉必定執行一次（autoplay 也不例外）
      recordSessionPayout(gameResult.mgResult.payout)
      const dropResult = gameResult.mgResult.dropList[dropIndex] as Drop
      const { isExpanding, dropTable } = dropResult
      if (isExpanding) await tableService.expandTable()
      useGameStore.setState({
        gameResult,
        isReadyToStop: true,
        tableList: dropTable,
        freeGameType: gameResult.mgResult.freeGameType ?? 0,
        freeGameConut: gameResult.mgResult.fgAddTimes,
      })
      // 這一輪完全沒有觸發 tumble（沒有經過 onRemove 迴圈）時，
      // 不用等 reel 完全落地，回彈一開始就能觸發 collect 表演。
      // 但 shouldDeferCollectCombo 成立時要讓給 onMainGameWinning，否則會收兩次
      const { dropList: mgDropList, multiplierList: mgMultiplierList, fgAddTimes: mgFgAddTimes } = gameResult.mgResult
      const noTumbleThisSpin = mgDropList.length <= 1
      const collectOnSpinIn = noTumbleThisSpin && !shouldDeferCollectCombo(mgDropList, mgMultiplierList, mgFgAddTimes)
      await spinService.spinIn(collectOnSpinIn
        ? () => {
            void collectComboIfNeeded()
          }
        : undefined)
    }),
    onMainGameWinning: fromPromise(async () => {
      const store = useGameStore.getState()
      const { payout, multiplierList, odds, fgAddTimes, dropList } = store.getGameResult()
      if (payout > 0) {
        if (multiplierList.length) {
          await payoutMultiplierForMainGame(payout, multiplierList)
        }
        if (odds > 20) {
          await sleep(800)
          await winningManager.showWinningPanel(odds, payout)
        }
        winningService.showTumblewinText(payout)
        await winningService.updateWinBoard(payout, false)
        await sleep(1000)
      }
      await collectComboAfterMultiplierWinIfNeeded(dropList, multiplierList, fgAddTimes)
      if (fgAddTimes > 0) {
        await sleep(800)
      }
      else {
        await finishMainGameWinning(payout)
      }
    }),
    onFreeGameWinning: fromPromise(async () => {
      const store = useGameStore.getState()
      const { payout, multiplierList, odds, fgAddTimes, dropList } = store.getGameResult()
      if (payout > 0) {
        if (multiplierList.length) {
          await payoutMultiplierForFreeGame(payout, multiplierList)
        }
        if (odds > 20) {
          await sleep(800)
          await winningManager.showWinningPanel(odds, payout)
        }
        winningService.showTumblewinText(payout)
        await winningService.updateWinBoard(payout, true)
      }
      await collectComboAfterMultiplierWinIfNeeded(dropList, multiplierList, fgAddTimes)
      if (fgAddTimes > 0) {
        // await sleep(3000)
      }
      else {
        await sleep(300)
      }
    }),
    onFreeSpinning: fromPromise(async () => {
      winningService.initFreeGame()
      const { isPlaying } = useAutoPlayStore.getState()
      const { isTurbo } = useTurboStore.getState()
      const { isExpand, dropIndex } = useGameStore.getState()
      if (isPlaying) {
        autoPlayService.reduceRemain()
        ga4Info.recordAutoPlay()
      }
      if (isTurbo) ga4Info.recordSpeedTurboMode()
      await spinService.spinOut()
      const gameResult = useGameStore.getState().getGameResult()
      const dropResult = gameResult?.dropList?.[dropIndex] as Drop
      const { isExpanding, dropTable } = dropResult
      if (!isExpand && isExpanding) await tableService.expandTable()
      useGameStore.setState({ isReadyToStop: true, tableList: dropTable, freeGameType: gameResult.freeGameType ?? 0 })
      // 這一輪完全沒有觸發 tumble（沒有經過 onRemove 迴圈）時，
      // 不用等 reel 完全落地，回彈一開始就能觸發 collect 表演。
      // 但 shouldDeferCollectCombo 成立時要讓給 onFreeGameWinning，否則會收兩次
      const { dropList: fgDropList, multiplierList: fgMultiplierList, fgAddTimes: fgAddTimesThisSpin } = gameResult
      const noTumbleThisSpin = fgDropList.length <= 1
      const collectOnSpinIn = noTumbleThisSpin && !shouldDeferCollectCombo(fgDropList, fgMultiplierList, fgAddTimesThisSpin)
      await spinService.spinIn(collectOnSpinIn
        ? () => {
            void collectComboIfNeeded()
          }
        : undefined)
    }),
    onFreeStopSpin: fromPromise(async () => {
      // no-op
    }),
    onFreeOnce: fromPromise(async () => {
      const store = useGameStore.getState()
      // 這一輪免費遊戲的派彩：onFreeOnce 每輪剛好執行一次，
      // 各輪加總等同 fgPayout，與主遊戲的 mgResult.payout 互不重複
      recordSessionPayout(store.getGameResult()?.payout ?? 0)
      useGameStore.setState({ freeGameIndex: store.freeGameIndex + 1 })
    }),
    onRemoveing: fromPromise(async () => {
      const gameStore = useGameStore.getState()
      const dropIndex = gameStore.dropIndex
      const dropItem = gameStore.getDropItem()
      await tableService.tableRemove(dropItem.totalWinPositions)
      const nextDropIndex = dropIndex + 1
      useGameStore.setState({ dropIndex: nextDropIndex })
      const newDropItem = gameStore.getDropItem()
      if (newDropItem.isExpanding) await tableService.expandTable()
      await tableService.playDropAnimation(dropItem.totalWinPositions, newDropItem.giftedSymbols)

      // 已經到達 dropList 的最後一項（不會再有下一輪 onRemoveing），
      // 這裡是「有觸發 tumble」情況下 collect 表演該播放的時機——
      // 但若這一輪同時有 multiplier symbol，collect 改延後到 onMainGameWinning/onFreeGameWinning
      // 表演完 multiplier 收集 + winbar 跑分後才觸發，這裡略過
      const { dropList, multiplierList, fgAddTimes } = gameStore.getGameResult()
      const isLastDrop = nextDropIndex >= dropList.length - 1
      if (isLastDrop && !shouldDeferCollectCombo(dropList, multiplierList, fgAddTimes)) {
        await collectComboIfNeeded()
      }
    }),
    onZeusAwarding: fromPromise(async () => {
      //
    }),
  },
  actions: {
    initGodziDropDashBoard: async ({ self }) => {
      self.send({ type: 'close godzi dashboard' })
      // godziDashboardService.init()
    },
    initVoucher: async () => {
      const slotVoucher = useVoucherStore.getState().tempSlotVoucher
      // 有未結算單待重播時先跳過 voucher 選擇 popup，等 replay 結束後
      // onInitGame 會再送 'check remain voucher' 回到這裡。
      if (useOngoingGameStore.getState().isOnGoingGame) {
        threecomboolympusMachine.send({ type: 'initVoucher finished' })
      }
      else if (slotVoucher !== null && slotVoucher !== undefined) {
        useVoucherStore.setState({
          currentVoucherId: slotVoucher.voucherId,
          fixedStake: slotVoucher.fixedStake,
          totalPayout: slotVoucher.winAmount,
          totalSpin: slotVoucher.freeSpin,
          remainSpin: slotVoucher.netFreeSpin,
          expireDate: slotVoucher.expireDate,
          isShowVoucherSelectPopup: true,
        })
      }
      else {
        threecomboolympusMachine.send({ type: 'initVoucher finished' })
      }
    },
    onInitGame: async () => {
      const gameStore = useGameStore.getState()
      const godziStore = useGodziDropStore.getState()
      const voucherStore = useVoucherStore.getState()
      const playerStore = usePlayerStore.getState()
      const gameStatus = gameStore.gameStatus
      const isVoucher = gameStore.isVoucher()
      const godziPrizeType = godziStore.type
      const hasGodziPrize = godziPrizeType !== GodziDropResultType.None
      if (gameStatus === GameStatus.Godzi) {
        return threecomboolympusMachine.send({ type: 'exit godzi drop' })
      }

      if (gameStatus === GameStatus.Normal && hasGodziPrize) {
        return threecomboolympusMachine.send({ type: 'start enter godzi drop' })
      }

      if (isVoucher && voucherStore.remainSpin === 0) {
        return threecomboolympusMachine.send({ type: 'show voucher total win' })
      }
      if (isReplayingOngoingGame) {
        isReplayingOngoingGame = false
        // 延遲結算單（有 FG/BG 或有贏分）動畫播完後在這裡結算，
        // settledBalance 會寫進 lastSettledBalance，再由下一行同步到 balance
        await endGameService.endGameIfNeeded()
        playerStore.updateBalanceToSettledAmount()
        // ongoing game 本身不是 voucher 單，但玩家還有待進入的 voucher：重播結束後接著進 voucher mode
        if (gameStatus === GameStatus.Normal && voucherStore.tempSlotVoucher !== null) {
          return threecomboolympusMachine.send({ type: 'check remain voucher' })
        }
        return
      }
      // 斷線重連恢復的未結算單：進入 init game 後自動重播，不重新下注
      if (useOngoingGameStore.getState().isOnGoingGame) {
        return threecomboolympusMachine.send({ type: 'start spin' })
      }
    },
    onInitFreeGame: async () => {
      autoPlayService.cancel()
      winningService.init()
      const { isExpand: wasExpanded } = useGameStore.getState()
      useGameStore.setState({ dropIndex: 0, freeGameIndex: 0 })
      if (wasExpanded) tableService.removeExpandTable()
      await freeGameService.playSceneIn()
    },
    onFreeTotalWin: async () => {
      freeGameService.playTotalWinIn()
      // free game 下單當下是延遲結算（balance 回 -1），整個免費遊戲期間都沒有機會結算；
      // total win 畫面一出現就在背景結算並同步 balance，不擋 total win 動畫本身
      void (async () => {
        await endGameService.endGameIfNeeded()
        usePlayerStore.getState().updateBalanceToSettledAmount()
      })()
    },
    displayVoucherTotalWin: async () => {
      await sleep(800)
      useVoucherStore.setState({ isShowVoucherTotalWinPopup: true })
    },
    dispatchGodziGame: async () => {
      useGameStore.setState({
        gameStatus: GameStatus.Godzi,
      })
      const godziPrizeType = useGodziDropStore.getState().type
      switch (godziPrizeType) {
        case GodziDropResultType.FreeGame:
          await godziDropService.enterFreeGamePrize()
          break
        case GodziDropResultType.FreeSpin:
          await godziDropService.enterFreeSpinPrize()
          break
        case GodziDropResultType.RatioPrize:
          await godziDropService.enterRatioPrize()
          break
      }
    },
    dispatchGodziWin: async () => {
      setTimeout(() => {
        videoRecordService.stopRecordingInGodziTotalWin()
      }, 100)
      await godziDropService.showGodziTotalWin()
    },
  },
  guards: {
    hasRemainSpin: () => {
      if (useGameStore.getState().gameStatus === GameStatus.Godzi) {
        return useGodziDropStore.getState().gameResultList.length > 0
      }
      else {
        const hasAutoPlayRemainSpin = useAutoPlayStore.getState().isPlaying
        const hasEnoughBalance = isPlayerHasEnoughBalance()

        const isVoucher = useGameStore.getState().isVoucher()
        if (hasAutoPlayRemainSpin && !hasEnoughBalance && !isVoucher) {
          setErrorPopupData(ErrorType.InsufficientBalance, { enableReload: false })
          useGameStore.setState({ betOptionType: BetOptionType.MainGame })
          autoPlayService.cancel()
        }

        return hasAutoPlayRemainSpin && hasEnoughBalance
      }
    },
    hasFreeGame: () => {
      const fgResultList = useGameStore.getState().gameResult.fgResultList
      return fgResultList.length > 0
    },
    hasRemainFreeSpin: () => {
      const remainFreeSpin = useGameStore.getState().getRemainFreeSpin()
      return remainFreeSpin > 0
    },
    hasRemove: () => {
      const removeList = useGameStore.getState().getDropItem().totalWinPositions
      return removeList.length > 1
    },
    hasAward: () => {
      return false
    },
    /** 主遊戲：這一轉有派彩才進 winning 表演。 */
    hasMainGameWinning: () => {
      const winPayout = useGameStore.getState().gameResult.totalPayout
      return winPayout > 0
    },
    /**
     * Free game：一律進 free winning。
     * 這裡不看派彩——free winning 除了跑分，還負責 multiplier 收集、
     * 延後的 collect combo 與 retrigger 表演，沒派彩的那幾轉同樣要走完。
     */
    hasFreeGameWinning: () => true,
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QBcAWAnMYDGB7AtgEa64A2AnvgA4CusAdMjcrugJYCGpAysh8mADETFuy4ACMADsIAbQAMAXUSgquWG2RtcUlSAAeiAIwAOAGz0ArEctmAzHZNGA7JbsAmZ84A0Icojt5SwAWeiN5Lztg4IBOJ2CvAF9E3zRMHAJiMkpaBjYpTQBxXAgALzYAEXRcKgqOWFQAIVwOdAhefiFsUnUwcSgS8vEIetRiVrklPTUNLR09QwQzcPo7SxjHe2C3ZyMzX38EQJCwiOco2PiklJA0rDwiEgpqOnp8zQA1XBpsVDB0DoCQTvZBfH5-dDiABm7wakAUyiQIBmmm0uiRi2Cdhi9DM1jMzg2lnk8nc8nsB0Q7hM7hx7g8Tmc8gclmpZmSqQw90yTxyrxB-Q4+CEsD46GQ4lgVHyCOm6lR8wxiAAtEY1aFye57PJgkETIT3JSEO5giZ5PRnCZglqdrtnOybncMo9si88gUJVAhSKxRLpAJIQMymxhtUqLKkSi5ujQIsPBYzDE3DE8cszJ5LEb6WZzWZYkZtoT1htnBzblznVlnrk3h7BcLBGB9Jp+oMQxAwxHVPLowsqSYTLiTSYYuTR6TPD4-FSzFbTpbdWYl9sHZz0g8q3z3S2vQ2GrgAO7iABu31+-3ELD4pHEB5lU0jPbRfeNlqHptHiZJ7knWZiptxCIzSCLVtjsMsnQ3Xk3VrHdvUEc9sAAa3ETB8A4fITzPCEu2RJ9FVjFVwlCalWQHLV6UcCIjQ8eQcV2clrEsEw7ALUtHQrKDXRrKV8gKKQoEECAdDAWtTyQ0TIJ5bjXl4qR+KgBB8lPbB+DRBFcKjZ8lQQZVzkHRNWSMdxmPTLwjCNUwtXodwbGcekCxHSIIM46Tq1k6V5PyQThKkUTlNwCTGFcl13IYOSFKUqQVLUnQNKMRFu1mbTCN0mJPCsYJZy1dLWI8TNpwQFw7AsNZrCywzYhiIwXPXNyt3oCLvMbdBqnQegqFIfgoVYfBgrq0KGqagSopi6MNIfJKFRjAwVTiZwwhTFwzUXWcYizDNcVYwlznsAlV3LAbNxgnQACUwHwXBjyEXz-OiwLJJC46azOi6rrAUbcFU8alE0-CZsWPTYnocxR3OOi1RNfZCoLSxLHoZkVyTKI9lq7lBpOqRzsu66hJEsSHv69HnteV6cY+gLvvU36ErlZKCNm3TnAA60TJCLLdXsyy7F2C1LXMYI9jh00TDRytoJerG3tx26CaCqSMcl7H3s+qm4t+9xErw+mAcQSwmVWVjqvOf9ohsSz5BsC1tl1LETRie0DoVkmGDvLyBLxvy5ceo6JdeN3Isp2KpAmrWtIZwHLSMMIspY2zsTVCkYZ1UIHYSEs6JA0WON9mTXb45rZYC+Wnr9-P3cUoOfsUWRacfHWX2M5kbNskltmJdvLNZ3E4lZVkTMLdwxa4sL6ADwv8eLn3ibLseC5GqvqZrzW6eml8WIWkcTDhljrBK6wjVHUJwkd9LBZRyxh-qmDx49ov7pL3PR9vyv7rVkPfrsMP-sboweZB00WVTD6zzPaSyexo5JnSnEa0+8EhX0Vq8PApBSA4GQAAYR5J7O64lp7izzvQZBqDsAYJ5KrYOodV69h0npDYIMyTnFbp4PEdgjT2XojqS2NJaSjn1ggl2hCyDENIY8bB3sib4NHkQtBmDHjkOrrXb+DcaEOwMkuaBBY8weGCEaOGdh6Aph1NYdKZJ1H8NntIkhsjiBiKnhIkeDVLEiOIPIpesgV71zXjQwk+j94Mi8AkEIrDCrWkHLlViP4tTaidqXAhUJ0iFG9LBCU8SsD1iEKkvou4+iilaBKOSf1lGpVsvSXE+plhxFBlzGG58rAkjxF4MkgstTmLiQkpJmTJSeQUrYh+eCHEwUyYk4U9BOnDVfmNNxk1tZeOKfreGOomRODVG4IxXcXB1IiMyEkxwbCtNHkMjp6Qunzx8pPPp9jr41kOSMsZ3TvKuPVjXOuU1qFzNcAjBI8hlk2ECNsSytILDGx4fYf8Xh2Jrhnm0rAwzRJ3NOb03BlzEEMBuXC454zHkf2Xko2ZjMTIfMWd8tUvy1kw1sOaEyxKmRGXTPshqaLRkYvuR7f4bUOpdWQD1dAfVnaz0ZfCiuWLKGeLefi+ZnylkktWf8mGiZQjplsNaBIDFL45yhQc9ptzjlk3eoiwmfLoVgFhUytJurrrCt+tM8OutjTQNWFiEszTY4ArWAjEkFKPDM3skPdVkiGVavRWaqW5N9WPw1QGmFRzg3KwtYvJ5iiqEpUZtowcTgtSuFcNaSI3NrJUuWHRewPMYlP0jca6NfRzU3XOUiw1mqo3apjdLCmb8KEa1xWKuMw4QbGQJPM7N5xXXRypayUB3yIj0sGYG01lapAAC0wB0AAIIHgmGG-pVzXgCp1fOxdsAV0TEtTXa1P8dKpp7Rm-tk5gmHALKxC0xJmbhCMP+WyJaI1TobUG2dC7l2rraOu5FAjt3Bt-fu-9EAj2JtFcmrtAF019qzdegFb5XBBFnHaCGarIX+s-eWxtfQX6AbrWWk1nSX5QZPUUlNNI6QNKxK4Ac9kb3GDhtHfUzEfzC1ZBOv1AzrnTvIwi++tbYn1vw9+28pyoMvJmZ2gItGbL0Z5sxfU9Iu7fLCCZbecNZwEg8JOgTX6Z3iB0NgPoMICgNGI2J0jFbTNSHM9CWEqBKMdtg4gaIP4hxuG3jmEc0RwHGXdUEIIURHamkM1uwTO6nOWbYNZkTBrbN4bI7FizLmZPuYjnrS2+jAFSpfdvFjRV+6ZUoimWwThmJRdRTFtJTjrG4Bs6W1L9nGtkPjdiwpeLMTd0VY4ClAWdHkusCDTNo4vV-14zh-j0XjOdKvBIN2CEeiwAs1AS8uBrxSexUmnLCBdODntJx5kTgPBrEsgSQcOofzM3MMxS4tWZ0mtXS2fcR4ls3hW-oXJAh6AcChAGAAFAOeQABKYQKWjMSbHhhfJqBDxbZ227Hr8nDsuFTl5v+5hdgcau2+EI9J9aBEiL62bm6GD+gvEGIYHYaiNmbJ6NsoYajQlYKhWK4gqDsFKGANHHmED2UHE09Klo3C6m+VmZVSm1ji5YtaEIz3qeBhZ-TqgghcnilbMGE5UhoQ9APALg7qp0w92WLR71Wp9QbQ8AjGI-4kxLnZiVZXTOdd07DJrxHR5adsDds5qzfxJjZdtdSAyw5Pzjju1OQ4Jl7CrH1laLwuV0PPdPOCf4AAVbbXAADq+QEJ-GQqhC6GF9cZ-POgY3trLTuFxHadMYO-5-z-ABHMloPWgTcMrqQAZoTHOyYIX7fB-uA5B03CHUPWs1hVwPtJ2Sa+NzhgtW2rJ7RoatIacl-81Rgu2jzAswRkg3CkCUOAegSNun27a1UVlDa3f04WmkNFBbWzsviPL+sIWHQ-TWEQrAnAPAo+YAN+L41oNEQQCqZwFwcQbEP+V+NYIIxQwYVQNQdQDQzQEwgIoBMGB2Dg9eGGo6cQDu9g5shUgQ96J89oZ2eY2Gv+uGSBHoYIVeOBYBOkz6FglsyYzMuoUQ1UWYZo8MjCW+6UpgrEx+fGlOyS6S7BqUd+oQOmVo2wNg+opo0uo4YQS4ngdEzEuwf8z24ychjMd+UQD+1oT+KYL+hUZI1k+ols1oJi7C5ODBc2DAVaxhgMcM9e0QuwOY2II4ywI2t6umuIbEJ86YYhz2L8nhKo4MYRbgngI4IEDu4CeIqwewPME2Dgz2HWjwsRTMJkBiKqTIJoO828RoI48M6wywL6IQxYA4z2aKBRwC0c6wjgaozMDgZ2FsJoPaVobgDgps9gTR06AonS2SLRI4OI7RyyXRlBJg3MmhxIYWeINs1gCB0O82sOgqCkBRlE9eSeWURkkSCQSx5oKxs4JU3yuw9BiB2xaWTa5M+xDsCYhI28ewOYdRsexg1IC0lxpIJOrgS4oxC2O6YGB6bQLxCe9orEam1I2IPMSx+iJIqx9RKhmxM+Dx9mMReBtq1xpU0QpIcQ8cNslke8WmzEBYewsQXCoJOx6WgeCWqABRpo5IF63CUCtg0Mt6lwnyP4WU4MVo2cFOKKL27WQiMiPIrJpgg4DIgSKMDgwRrGGUKmSYE4FwmJf+2JBGyOy2+QBRbgaoBiiYBYnC3+PJxgeICysCJIWaNgP49Jr28OkoPuep32BpeJL4xICen+-4ZpjGBUIRDuWhwuz6PM5g76jBrwc+furOVABReIOIZotIuhtgBYcQG0cQIMpIsMA4FSLh9xVO7ucZ6uLRzI9eeOSZpI2i+sWYDgC0GZZRtk6wk46e2E2euepABeM0Nq3pdEVgneL6S4C4hIf4RRHeNIeYbEwsve-eEx3oLRjgGo-m3hgQKYmZO+8MoMgC+sQCTgJ+iQQAA */
  id: 'threecomboolympus',
  initial: 'tutorialState',
  states: {
    'tutorialState': {
      on: {
        'tutorial end': 'initGodziDropDashBoardState',
      },
    },

    'initGodziDropDashBoardState': {
      entry: 'initGodziDropDashBoard',

      on: {
        'close godzi dashboard': 'initVoucherState',
      },
    },

    'initVoucherState': {
      on: {
        'initVoucher finished': 'init game',
      },

      entry: 'initVoucher',
    },

    'init game': {
      entry: 'onInitGame',

      on: {
        'start spin': 'spinning',
        'start enter godzi drop': 'enter godzi drop',
        'exit godzi drop': 'exit godzi drop',
        'show voucher total win': 'voucherTotalWin',
        // ongoing game 重播結束後，若還有待進入的 voucher 就轉去 voucher 流程
        'check remain voucher': 'initVoucherState',
      },
    },

    'spinning': {
      invoke: {
        src: 'onSpinning',
        // onDone: 'stopSpin',
        onDone: [{ target: 'onRemove', guard: 'hasRemove', reenter: true }, 'winning'],
        onError: 'init game',
      },
    },

    'onRemove': {
      invoke: {
        src: 'onRemoveing',
        onDone: [
          { target: 'onRemove', reenter: true, guard: 'hasRemove' },
          { target: 'winning', reenter: true, guard: 'hasMainGameWinning' },
          { target: 'init game', reenter: true },
        ],
      },
    },

    'winning': {
      invoke: {
        src: 'onMainGameWinning',
        onDone: [
          { target: 'enter free game', reenter: true, guard: 'hasFreeGame' },
          { target: 'spinning', reenter: true, guard: 'hasRemainSpin' },
          { target: 'init game', reenter: true },
        ],
      },
    },

    'freeGame': {
      initial: 'init free game',
      states: {
        'init free game': {
          entry: 'onInitFreeGame',
          on: { 'free game start spin': 'free spinning' },
        },

        'free spinning': {
          invoke: {
            src: 'onFreeSpinning',
            onDone: [
              { target: 'free onZeusAward', reenter: true, guard: 'hasAward' },
              { target: 'free onRemove', reenter: true, guard: 'hasRemove' },
              { target: 'free winning' },
            ],
            onError: {
              target: 'wait show total win',
              reenter: true,
            },
          },
        },

        'free onRemove': {
          invoke: {
            src: 'onRemoveing',
            onDone: [
              { target: 'free onRemove', reenter: true, guard: 'hasRemove' },
              { target: 'free winning', reenter: true, guard: 'hasFreeGameWinning' },
              { target: 'free once finish', reenter: true },
            ],
          },
        },

        'free onZeusAward': {
          invoke: {
            src: 'onZeusAwarding',
            onDone: [
              { target: 'free winning', reenter: true, guard: 'hasFreeGameWinning' },
              { target: 'free once finish', reenter: true },
            ],
          },
        },

        'free winning': {
          invoke: {
            src: 'onFreeGameWinning',
            onDone: { target: 'free once finish', reenter: true },
          },
        },

        'free once finish': {
          invoke: {
            src: 'onFreeOnce',
            onDone: [
              { target: 'free spinning', guard: 'hasRemainFreeSpin', reenter: true },
              {
                target: 'wait show total win',
                reenter: true,
              },
            ],
          },
        },

        'free total win': {
          entry: 'onFreeTotalWin',
          on: { 'close fg total win': '#threecomboolympus.init game' },
        },

        'wait show total win': {
          after: {
            880: 'free total win',
          },
        },
      },
    },

    'enter godzi drop': {
      on: {
        'exit godzi drop for ratio prize': 'exit godzi drop',
        'start godzi spin flow': { target: 'spinning', reenter: true },
      },

      entry: 'dispatchGodziGame',
    },

    'exit godzi drop': {
      entry: 'dispatchGodziWin',

      on: {
        'show godziwin finished': { target: 'init game', reenter: true },
      },
    },

    'voucherTotalWin': {
      on: {
        'check remain voucher': 'initVoucherState',
      },

      entry: 'displayVoucherTotalWin',
    },

    'enter free game': {
      after: {
        1200: 'freeGame.init free game',
      },
    },
  },
})

export const threecomboolympusMachine = createActor(machine)

let lastStateChangeAt = performance.now()

function logStateEnter(stateValue: unknown) {
  const now = performance.now()
  const elapsedMs = Math.round(now - lastStateChangeAt)
  lastStateChangeAt = now
  if (!import.meta.env.DEV) return
  console.log(
    `%c[machine] enter ${JSON.stringify(stateValue)} (prev state lasted ${elapsedMs}ms)`,
    'color: black; background: #81D8D0; border-radius: 5px',
  )
}

threecomboolympusMachine.subscribe(state => logStateEnter(state.value))
logStateEnter(threecomboolympusMachine.getSnapshot().value)
threecomboolympusMachine.start()
